// src/pages/Onboarding.tsx: first-run setup for managers with no firm yet.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../services/supabase';
import LocationSelect from '../components/LocationSelect';

const C = {
  navy:    '#0A1628', navyDark:'#091A3E', gold:    '#C9A84C',
  white:   '#FFFFFF', surface: '#F5F6F8', border:  '#D0D5DD',
  borderLt:'#E8EBF0', text:    '#0A0A0A', textSec: '#3D4552',
  muted:   '#6B7280', mutedLt: '#9CA3AF', danger:  '#C0222A',
};

const FIRM_TYPES = [
  { value: 'hedge_fund',     label: 'Hedge Fund' },
  { value: 'pe',             label: 'Private Equity' },
  { value: 'vc',             label: 'Venture Capital' },
  { value: 'real_estate',    label: 'Real Estate' },
  { value: 'credit',         label: 'Credit' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'multi_strategy', label: 'Multi-Strategy' },
];

const ASSET_CLASSES = [
  { value: 'hedge_fund',     label: 'Hedge Fund' },
  { value: 'pe',             label: 'Private Equity' },
  { value: 'vc',             label: 'Venture Capital' },
  { value: 're',             label: 'Real Estate' },
  { value: 'credit',         label: 'Credit' },
  { value: 'infrastructure', label: 'Infrastructure' },
];

// AUM bands map onto the firms.aum_min / aum_max columns, both in $mm.
const AUM_RANGES = [
  { value: '0-50',      label: 'Under $50m',   min: 0,    max: 50 },
  { value: '50-100',    label: '$50m - $100m', min: 50,   max: 100 },
  { value: '100-250',   label: '$100m - $250m', min: 100, max: 250 },
  { value: '250-500',   label: '$250m - $500m', min: 250, max: 500 },
  { value: '500-1000',  label: '$500m - $1bn', min: 500,  max: 1000 },
  { value: '1000-5000', label: '$1bn - $5bn',  min: 1000, max: 5000 },
  { value: '5000+',     label: 'Over $5bn',    min: 5000, max: null },
];

const STEPS = ['Welcome', 'Firm details', 'First fund', 'Complete'];

const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white,
  border: `1px solid ${C.border}`, borderRadius: '3px',
  color: C.text, fontSize: '13px',
  padding: '8px 10px', outline: 'none',
  fontFamily: 'system-ui', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '10px',
  fontWeight: 700, color: C.muted,
  marginBottom: '5px', letterSpacing: '0.6px',
  textTransform: 'uppercase', fontFamily: 'system-ui',
};

const cardStyle: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', padding: '32px 34px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

const primaryBtn: React.CSSProperties = {
  background: C.navy, border: 'none', borderRadius: '3px',
  color: C.white, fontSize: '13px', fontWeight: 600,
  padding: '11px 22px', cursor: 'pointer',
  fontFamily: 'system-ui', letterSpacing: '0.2px',
};

const ghostBtn: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px',
  color: C.textSec, fontSize: '13px', fontWeight: 500,
  padding: '11px 18px', cursor: 'pointer', fontFamily: 'system-ui',
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: '14px' }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, firm, loading } = useProfile();

  const [step, setStep]       = useState(1);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [firmId, setFirmId]   = useState<string | null>(null);
  const [fundSaved, setFundSaved] = useState(false);

  const [firmForm, setFirmForm] = useState({
    name: '', type: '', country: '', city: '',
    aum_range: '', website: '', description: '',
  });

  const [fundForm, setFundForm] = useState({
    name: '', asset_class: '', strategy: '',
    management_fee: '', performance_fee: '',
    minimum_subscription: '', redemption_period: '',
  });

  // Anyone who does not need onboarding goes straight to their dashboard.
  useEffect(() => {
    if (loading || !profile) return;
    if (profile.role !== 'manager' || firm) navigate('/dashboard', { replace: true });
  }, [loading, profile, firm, navigate]);

  const updateFirm = (field: string, value: string) => setFirmForm(f => ({ ...f, [field]: value }));
  const updateFund = (field: string, value: string) => setFundForm(f => ({ ...f, [field]: value }));

  const focusStyle = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    (e.target.style.borderColor = C.navy);
  const blurStyle = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    (e.target.style.borderColor = C.border);

  const selectedAum = AUM_RANGES.find(r => r.value === firmForm.aum_range);
  const headquarters = [firmForm.city, firmForm.country].filter(Boolean).join(', ');

  const saveFirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true); setError('');
    const { data, error: firmErr } = await supabase.from('firms').insert({
      name: firmForm.name.trim(),
      type: firmForm.type,
      headquarters,
      aum_min: selectedAum ? selectedAum.min : null,
      aum_max: selectedAum ? selectedAum.max : null,
      website: firmForm.website.trim() || null,
      description: firmForm.description.trim() || null,
      owner_id: user.id,
    }).select().single();

    if (firmErr || !data) {
      setError(firmErr?.message || 'Could not create your firm. Please try again.');
      setSaving(false);
      return;
    }

    // Owner row so team management works from day one.
    await supabase.from('firm_members').insert({
      firm_id: data.id, user_id: user.id, role: 'owner', added_by: user.id,
    });

    setFirmId(data.id);
    setSaving(false);
    setStep(3);
  };

  const saveFund = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firmId) { setError('No firm found. Please complete the previous step.'); return; }
    setSaving(true); setError('');
    const { error: fundErr } = await supabase.from('funds').insert({
      firm_id: firmId,
      name: fundForm.name.trim(),
      asset_class: fundForm.asset_class,
      strategy: fundForm.strategy.trim(),
      headquarters,
      management_fee: fundForm.management_fee ? parseFloat(fundForm.management_fee) : null,
      performance_fee: fundForm.performance_fee ? parseFloat(fundForm.performance_fee) : null,
      minimum_subscription: fundForm.minimum_subscription ? parseFloat(fundForm.minimum_subscription) : null,
      redemption_period: fundForm.redemption_period.trim(),
      status: 'open',
    });
    if (fundErr) { setError(fundErr.message); setSaving(false); return; }
    setFundSaved(true);
    setSaving(false);
    setStep(4);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading...</div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh', background: C.surface,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      padding: '40px 24px 64px',
    }}>
      <div style={{ maxWidth: '620px', margin: '0 auto' }}>

        {/* ── Brand ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '28px' }}>
          <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
            <path d="M 4 14 A 10 10 0 0 1 24 14" stroke="#0D6E8A" strokeWidth="2.2" strokeLinecap="round"/>
            <path d="M 6 14 A 8 8 0 0 0 22 14" stroke={C.gold} strokeWidth="2.2" strokeLinecap="round"/>
            <circle cx="14" cy="14" r="2.2" fill={C.gold}/>
          </svg>
          <span style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '18px', color: C.navy }}>Altveno</span>
          <span style={{
            fontSize: '9px', color: C.gold, letterSpacing: '1.8px',
            fontWeight: 700, textTransform: 'uppercase', marginLeft: '4px',
          }}>Manager Setup</span>
        </div>

        {/* ── Progress ── */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            {STEPS.map((label, i) => (
              <div key={label} style={{
                fontSize: '10px', fontWeight: 700, letterSpacing: '0.6px',
                textTransform: 'uppercase',
                color: i + 1 <= step ? C.navy : C.mutedLt,
              }}>
                {i + 1}. {label}
              </div>
            ))}
          </div>
          <div style={{ height: '3px', background: C.borderLt, borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${(step / STEPS.length) * 100}%`,
              background: C.gold, transition: 'width 0.25s ease',
            }} />
          </div>
        </div>

        {error && (
          <div style={{
            background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px',
            padding: '10px 14px', marginBottom: '16px', fontSize: '12.5px', color: C.danger,
          }}>
            {error}
          </div>
        )}

        {/* ── Step 1: Welcome ── */}
        {step === 1 && (
          <div style={cardStyle}>
            <h1 style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: '28px', color: C.text, fontWeight: 400,
              margin: '0 0 10px', letterSpacing: '-0.3px',
            }}>
              Welcome to Altveno
            </h1>
            <p style={{ fontSize: '14px', color: C.textSec, lineHeight: 1.7, margin: '0 0 18px' }}>
              Altveno connects alternative investment managers with institutional allocators.
              Once your firm and funds are on the platform you can raise capital, run due
              diligence and benchmark your terms from one place.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 26px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
              {[
                'A professional firm and fund profile visible to qualified allocators',
                'AI-powered DDQs and a secure data room with per-allocator access',
                'Investor pipeline tracking from first meeting through to investment',
                'Anonymous peer benchmarking across fees, AUM and strategy',
              ].map(item => (
                <li key={item} style={{ display: 'flex', gap: '9px', alignItems: 'flex-start', fontSize: '13px', color: C.textSec }}>
                  <span style={{
                    width: '16px', height: '16px', borderRadius: '50%', background: C.navy,
                    color: C.white, fontSize: '9px', fontWeight: 700, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px',
                  }}>✓</span>
                  {item}
                </li>
              ))}
            </ul>
            <button style={primaryBtn} onClick={() => setStep(2)}>
              Let's set up your firm profile
            </button>
          </div>
        )}

        {/* ── Step 2: Firm details ── */}
        {step === 2 && (
          <form style={cardStyle} onSubmit={saveFirm}>
            <h2 style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: '22px', color: C.text, fontWeight: 400, margin: '0 0 4px',
            }}>
              Firm details
            </h2>
            <p style={{ fontSize: '13px', color: C.muted, margin: '0 0 22px' }}>
              This is what allocators see first. You can refine it later from Manage Firm.
            </p>

            <Field label="Firm name">
              <input style={inputStyle} type="text" value={firmForm.name} required
                onChange={e => updateFirm('name', e.target.value)}
                placeholder="e.g. Meridian Capital"
                onFocus={focusStyle} onBlur={blurStyle} />
            </Field>

            <Field label="Firm type">
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={firmForm.type} required
                onChange={e => updateFirm('type', e.target.value)}
                onFocus={focusStyle} onBlur={blurStyle}>
                <option value="">Select type...</option>
                {FIRM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>

            <div style={{ marginBottom: '14px' }}>
              <LocationSelect
                country={firmForm.country} city={firmForm.city}
                onCountryChange={v => updateFirm('country', v)}
                onCityChange={v => updateFirm('city', v)}
                labelStyle={labelStyle}
              />
            </div>

            <Field label="AUM range">
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={firmForm.aum_range}
                onChange={e => updateFirm('aum_range', e.target.value)}
                onFocus={focusStyle} onBlur={blurStyle}>
                <option value="">Select range...</option>
                {AUM_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>

            <Field label="Website (optional)">
              <input style={inputStyle} type="text" value={firmForm.website}
                onChange={e => updateFirm('website', e.target.value)}
                placeholder="https://yourfirm.com"
                onFocus={focusStyle} onBlur={blurStyle} />
            </Field>

            <Field label="Brief description (optional)">
              <textarea value={firmForm.description}
                onChange={e => updateFirm('description', e.target.value)}
                placeholder="Who you are, what you invest in and what makes the firm distinctive..."
                style={{ ...inputStyle, height: '92px', resize: 'vertical' }}
                onFocus={focusStyle} onBlur={blurStyle} />
            </Field>

            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button type="button" style={ghostBtn} onClick={() => setStep(1)}>Back</button>
              <button type="submit" style={{ ...primaryBtn, opacity: saving ? 0.7 : 1 }} disabled={saving}>
                {saving ? 'Saving...' : 'Next'}
              </button>
            </div>
          </form>
        )}

        {/* ── Step 3: First fund ── */}
        {step === 3 && (
          <form style={cardStyle} onSubmit={saveFund}>
            <h2 style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: '22px', color: C.text, fontWeight: 400, margin: '0 0 4px',
            }}>
              Add your first fund
            </h2>
            <p style={{ fontSize: '13px', color: C.muted, margin: '0 0 22px' }}>
              Listing a fund makes you discoverable on AltConnect. You can add more funds later.
            </p>

            <Field label="Fund name">
              <input style={inputStyle} type="text" value={fundForm.name} required
                onChange={e => updateFund('name', e.target.value)}
                placeholder="e.g. Meridian Global Opportunities Fund"
                onFocus={focusStyle} onBlur={blurStyle} />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Field label="Asset class">
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={fundForm.asset_class} required
                  onChange={e => updateFund('asset_class', e.target.value)}
                  onFocus={focusStyle} onBlur={blurStyle}>
                  <option value="">Select...</option>
                  {ASSET_CLASSES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </Field>
              <Field label="Strategy">
                <input style={inputStyle} type="text" value={fundForm.strategy}
                  onChange={e => updateFund('strategy', e.target.value)}
                  placeholder="e.g. Long/Short Equity"
                  onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Field label="Management fee (%)">
                <input style={inputStyle} type="number" step="0.01" value={fundForm.management_fee}
                  onChange={e => updateFund('management_fee', e.target.value)} placeholder="1.75"
                  onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
              <Field label="Performance fee (%)">
                <input style={inputStyle} type="number" step="0.01" value={fundForm.performance_fee}
                  onChange={e => updateFund('performance_fee', e.target.value)} placeholder="20"
                  onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Field label="Minimum subscription ($mm)">
                <input style={inputStyle} type="number" step="0.1" value={fundForm.minimum_subscription}
                  onChange={e => updateFund('minimum_subscription', e.target.value)} placeholder="5"
                  onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
              <Field label="Redemption period">
                <input style={inputStyle} type="text" value={fundForm.redemption_period}
                  onChange={e => updateFund('redemption_period', e.target.value)}
                  placeholder="e.g. Quarterly with 45 days notice"
                  onFocus={focusStyle} onBlur={blurStyle} />
              </Field>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button type="button" style={ghostBtn} onClick={() => setStep(4)}>Skip for now</button>
              <button type="submit" style={{ ...primaryBtn, opacity: saving ? 0.7 : 1 }} disabled={saving}>
                {saving ? 'Saving...' : 'Next'}
              </button>
            </div>
          </form>
        )}

        {/* ── Step 4: Complete ── */}
        {step === 4 && (
          <div style={cardStyle}>
            <h2 style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: '26px', color: C.text, fontWeight: 400, margin: '0 0 10px',
            }}>
              You're all set!
            </h2>
            <p style={{ fontSize: '14px', color: C.textSec, lineHeight: 1.7, margin: '0 0 22px' }}>
              Your profile is live on Altveno. Here is what we created for you.
            </p>

            <div style={{ border: `1px solid ${C.border}`, borderRadius: '4px', marginBottom: '22px' }}>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.borderLt}` }}>
                <div style={{ ...labelStyle, marginBottom: '4px' }}>Firm</div>
                <div style={{ fontSize: '13px', color: C.text, fontWeight: 600 }}>{firmForm.name || '-'}</div>
                <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>
                  {[FIRM_TYPES.find(t => t.value === firmForm.type)?.label, headquarters, selectedAum?.label]
                    .filter(Boolean).join(' · ') || 'Details can be added from Manage Firm'}
                </div>
              </div>
              <div style={{ padding: '12px 16px' }}>
                <div style={{ ...labelStyle, marginBottom: '4px' }}>Fund</div>
                {fundSaved ? (
                  <>
                    <div style={{ fontSize: '13px', color: C.text, fontWeight: 600 }}>{fundForm.name}</div>
                    <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>
                      {[ASSET_CLASSES.find(a => a.value === fundForm.asset_class)?.label, fundForm.strategy]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: '12.5px', color: C.muted }}>
                    No fund added yet. You can list one any time from Manage Funds.
                  </div>
                )}
              </div>
            </div>

            <p style={{ fontSize: '13px', color: C.muted, lineHeight: 1.7, margin: '0 0 22px' }}>
              Next: complete your DDQ, upload documents to your data room, and start connecting
              with allocators on AltConnect.
            </p>

            <button style={primaryBtn} onClick={() => { window.location.href = '/dashboard'; }}>
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
